import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { useCatalogStore } from './catalog';
import { emitEvent } from '@/utils/eventBus';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_PAGE_SIZE = 25;

const normalizeRelation = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		name: attributes.name ?? data.name ?? '',
		address: attributes.address ?? data.address ?? '',
		documentId: attributes.documentId ?? data.documentId ?? null,
		rightToWorkEngagementType: attributes.rightToWorkEngagementType ?? data.rightToWorkEngagementType ?? null,
		engagementHistory: attributes.engagementHistory ?? data.engagementHistory ?? [],
	};
};

const normalizeServiceRelation = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? data.documentId ?? null,
		name: attributes.name ?? data.name ?? '',
		rate: attributes.rate ?? data.rate ?? null,
		supplierRate: attributes.supplierRate ?? data.supplierRate ?? null,
		employeeRate: attributes.employeeRate ?? data.employeeRate ?? null,
	};
};

const normalizeTimeEntry = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		startAt: attributes.startAt ?? data.startAt ?? null,
		endAt: attributes.endAt ?? data.endAt ?? null,
		durationMinutes: attributes.durationMinutes ?? data.durationMinutes ?? null,
		durationSeconds: attributes.durationSeconds ?? data.durationSeconds ?? null,
		startLocation: attributes.startLocation ?? data.startLocation ?? null,
		stopLocation: attributes.stopLocation ?? data.stopLocation ?? null,
		flagCodes: Array.isArray(attributes.flagCodes ?? data.flagCodes)
			? attributes.flagCodes ?? data.flagCodes
			: [],
	};
};

const normalizeTimeEntries = (payload) => {
	if (!payload) return [];
	const entries = Array.isArray(payload?.data)
		? payload.data
		: Array.isArray(payload)
			? payload
			: [];
	return entries.map(normalizeTimeEntry).filter(Boolean);
};

const normalizeAppointment = (entry) => {
	const attributes = entry?.attributes || entry || {};
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		date: attributes.date ?? null,
		hours: attributes.hours ?? null,
		status: attributes.status ?? 'assigned',
		trackTime: attributes.trackTime ?? null,
		fixedHours: attributes.fixedHours ?? null,
		scheduledStartAt: attributes.scheduledStartAt ?? null,
		scheduledEndAt: attributes.scheduledEndAt ?? null,
		actualStartAt: attributes.actualStartAt ?? null,
		actualEndAt: attributes.actualEndAt ?? null,
		actualMinutes: attributes.actualMinutes ?? null,
		actualSeconds: attributes.actualSeconds ?? null,
		completedAt: attributes.completedAt ?? null,
		flagCodes: Array.isArray(attributes.flagCodes) ? attributes.flagCodes : [],
		timeAdjustments: Array.isArray(attributes.timeAdjustments) ? attributes.timeAdjustments : [],
		timeEntries: normalizeTimeEntries(attributes.time_entries ?? attributes.timeEntries),
		flatRef: attributes.flatRef ?? '',
		building: normalizeRelation(attributes.building),
		supplier: normalizeRelation(attributes.supplier),
		service: normalizeServiceRelation(attributes.service),
		journal: normalizeRelation(attributes.journal),
	};
};

const normalizeIsoDate = (value) => {
	if (!value) return null;
	const str = String(value);
	return str.length >= 10 ? str.slice(0, 10) : str;
};

const normalizeHoursValue = (value) => {
	if (value === '' || value === null || value === undefined) return null;
	const numeric = Number(value);
	if (Number.isNaN(numeric)) return null;
	if (numeric < 0) {
		throw new Error('Hours cannot be negative');
	}
	return numeric;
};

const toUniqueStrings = (values = []) =>
	[...new Set((values || []).map((v) => (v === null || v === undefined ? '' : String(v))).filter(Boolean))];

export const useJournalAppointmentsStore = defineStore('journalAppointments', {
	state: () => ({
		items: [],
		isLoading: false,
		error: null,
		pagination: {
			page: 1,
			pageSize: DEFAULT_PAGE_SIZE,
			total: 0,
			pageCount: 1,
		},
		filters: {
			journalId: null,
			buildingId: null,
			supplierId: null,
			serviceId: null,
			flatRef: '',
			dateFrom: null,
			dateTo: null,
		},
		weekStartByJournal: {},
	}),
	getters: {
		buildingOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.buildingOptions;
		},
		supplierOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.supplierOptions;
		},
		serviceOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.serviceOptions;
		},
		weekStartForJournal: (state) => (journalId) =>
			journalId ? state.weekStartByJournal[journalId] || null : null,
	},
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		setWeekStartForJournal(journalId, isoDate) {
			if (!journalId) return;
			this.weekStartByJournal = {
				...this.weekStartByJournal,
				[journalId]: isoDate,
			};
		},

		buildQueryParams(overrides = {}) {
			const page = overrides.page ?? this.pagination.page;
			const pageSize = overrides.pageSize ?? this.pagination.pageSize;
			const params = {
				'pagination[page]': page,
				'pagination[pageSize]': pageSize,
				'sort[0]': 'date:desc',
				'populate[building][fields][0]': 'name',
				'populate[building][fields][1]': 'address',
				'populate[supplier][fields][0]': 'name',
				'populate[supplier][fields][1]': 'rightToWorkEngagementType',
				'populate[supplier][fields][2]': 'engagementHistory',
				'populate[service][fields][0]': 'name',
				'populate[service][fields][1]': 'rate',
				'populate[service][fields][2]': 'supplierRate',
				'populate[service][fields][3]': 'employeeRate',
				'populate[journal][fields][0]': 'name',
				'populate[time_entries][fields][0]': 'startAt',
				'populate[time_entries][fields][1]': 'endAt',
				'populate[time_entries][fields][2]': 'durationMinutes',
				'populate[time_entries][fields][3]': 'durationSeconds',
				'populate[time_entries][fields][4]': 'stopLocation',
				'populate[time_entries][fields][5]': 'flagCodes',
				'populate[time_entries][fields][6]': 'startLocation',
			};

			const { filters } = this;
			if (filters.journalId) {
				params['filters[journal][id][$eq]'] = filters.journalId;
			}
			if (filters.journalName) {
				params['filters[journal][name][$eq]'] = filters.journalName;
			}
			if (filters.buildingId) {
				params['filters[building][id][$eq]'] = filters.buildingId;
			}
			if (filters.supplierId) {
				params['filters[supplier][id][$eq]'] = filters.supplierId;
			}
			if (filters.serviceId) {
				params['filters[service][id][$eq]'] = filters.serviceId;
			}
			if (filters.flatRef?.trim()) {
				params['filters[flatRef][$containsi]'] = filters.flatRef.trim();
			}
			if (filters.dateFrom) {
				params['filters[date][$gte]'] = filters.dateFrom;
			}
			if (filters.dateTo) {
				params['filters[date][$lte]'] = filters.dateTo;
			}

			return params;
		},

		async fetchAppointments(options = {}) {
			if (this.isLoading) return;
			this.isLoading = true;
			this.error = null;

			const headers = this.authHeaders();
			const params = this.buildQueryParams(options);

			try {
				const response = await axios.get(
					`${STRAPI_URL}/api/journal-appointments-items`,
					{ headers, params },
				);

				const entries = response?.data?.data || [];
				this.items = entries.map(normalizeAppointment);

				const pagination = response?.data?.meta?.pagination || {};
				this.pagination = {
					page: pagination.page ?? this.pagination.page,
					pageSize: pagination.pageSize ?? this.pagination.pageSize,
					total: pagination.total ?? this.pagination.total,
					pageCount: pagination.pageCount ?? this.pagination.pageCount,
				};
			} catch (error) {
				this.error =
					error?.response?.data?.error?.message ||
					error.message ||
					'Failed to load appointments';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},

		async updateFilters(partial = {}) {
			this.filters = { ...this.filters, ...partial };
			this.pagination = { ...this.pagination, page: 1 };
			await this.fetchAppointments({ page: 1 });
		},

		async changePage(page) {
			if (page < 1 || page === this.pagination.page || page > this.pagination.pageCount) {
				return;
			}
			this.pagination = { ...this.pagination, page };
			await this.fetchAppointments({ page });
		},

		async refresh() {
			await this.fetchAppointments();
		},

		async initialize() {
			const catalogStore = useCatalogStore();
			if (!catalogStore.initialized) {
				await catalogStore.bootstrap?.();
			}
			await this.fetchAppointments();
		},

		async queryAppointmentsInRange({
			startDate,
			endDate,
			journalId,
			journalName,
			filters = {},
			pageSize = 100,
		}) {
			const headers = this.authHeaders();
			const safePageSize = Math.min(
				100,
				Math.max(1, Number.isFinite(Number(pageSize)) ? Number(pageSize) : 100),
			);
			const baseParams = {
				// Strapi maxLimit is configured to 100 on this project.
				'pagination[pageSize]': safePageSize,
				'sort[0]': 'date:asc',
				'populate[building][fields][0]': 'name',
				'populate[building][fields][1]': 'address',
				'populate[supplier][fields][0]': 'name',
				'populate[supplier][fields][1]': 'rightToWorkEngagementType',
				'populate[supplier][fields][2]': 'engagementHistory',
				'populate[service][fields][0]': 'name',
				'populate[service][fields][1]': 'rate',
				'populate[service][fields][2]': 'supplierRate',
				'populate[service][fields][3]': 'employeeRate',
				'populate[journal][fields][0]': 'name',
				'populate[time_entries][fields][0]': 'startAt',
				'populate[time_entries][fields][1]': 'endAt',
				'populate[time_entries][fields][2]': 'durationMinutes',
				'populate[time_entries][fields][3]': 'durationSeconds',
				'populate[time_entries][fields][4]': 'stopLocation',
				'populate[time_entries][fields][5]': 'flagCodes',
				'populate[time_entries][fields][6]': 'startLocation',
			};

			if (startDate) {
				baseParams['filters[date][$gte]'] = startDate;
			}
			if (endDate) {
				baseParams['filters[date][$lte]'] = endDate;
			}
			if (journalId) {
				baseParams['filters[journal][id][$eq]'] = journalId;
			} else if (journalName) {
				baseParams['filters[journal][name][$eq]'] = journalName;
			}
			if (Array.isArray(filters.buildingIds) && filters.buildingIds.length) {
				baseParams['filters[building][id][$in]'] = filters.buildingIds;
			} else if (filters.buildingId) {
				baseParams['filters[building][id][$eq]'] = filters.buildingId;
			}
			if (filters.supplierId) {
				baseParams['filters[supplier][id][$eq]'] = filters.supplierId;
			}
			if (filters.serviceId) {
				baseParams['filters[service][id][$eq]'] = filters.serviceId;
			}
			if (Array.isArray(filters.serviceGroupIds) && filters.serviceGroupIds.length) {
				baseParams['filters[service][services_groups][id][$in]'] = filters.serviceGroupIds;
			} else if (filters.serviceGroupId) {
				baseParams['filters[service][services_groups][id][$eq]'] = filters.serviceGroupId;
			}
			if (Array.isArray(filters.statuses) && filters.statuses.length) {
				baseParams['filters[status][$in]'] = filters.statuses;
			} else if (filters.status) {
				baseParams['filters[status][$eq]'] = filters.status;
			}
			if (Array.isArray(filters.excludeStatuses) && filters.excludeStatuses.length) {
				baseParams['filters[status][$notIn]'] = filters.excludeStatuses;
			}
			if (filters.flatRef?.trim()) {
				baseParams['filters[flatRef][$containsi]'] = filters.flatRef.trim();
			}

			const collected = [];
			let page = 1;
			let pageCount = 1;

			do {
				const response = await axios.get(`${STRAPI_URL}/api/journal-appointments-items`, {
					headers,
					params: {
						...baseParams,
						'pagination[page]': page,
					},
				});

				const entries = response?.data?.data || [];
				collected.push(...entries);

				const pagination = response?.data?.meta?.pagination;
				pageCount = pagination?.pageCount ?? page;
				page += 1;
			} while (page <= pageCount);

			return collected.map(normalizeAppointment);
		},

		// The office stops a timer a cleaner forgot, or corrects when a job started and stopped.
		async setTrackedTime({ id, startAt, endAt }) {
			const response = await axios.post(
				`${STRAPI_URL}/api/journal-appointments-items/${encodeURIComponent(id)}/tracked-time`,
				{ startAt, endAt },
				{ headers: this.authHeaders() },
			);
			return response?.data?.appointment || null;
		},

		async fetchAppointmentsInRange({ startDate, endDate, journalId, journalName, filters = {} }) {
			this.isLoading = true;
			this.error = null;

			try {
				const normalized = await this.queryAppointmentsInRange({
					startDate,
					endDate,
					journalId,
					journalName,
					filters,
				});

				this.items = normalized;
				this.pagination = {
					page: 1,
					pageSize: normalized.length,
					total: normalized.length,
					pageCount: 1,
				};
				return normalized;
			} catch (error) {
				this.error =
					error?.response?.data?.error?.message ||
					error.message ||
					'Failed to load appointments';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},

		async createAppointment({
			date,
			journalId,
			buildingId = null,
			supplierId = null,
			serviceId = null,
			hours = null,
			flatRef = '',
			status = null,
		}) {
			if (!date || !journalId) {
				throw new Error('Missing date or journal ID');
			}
			const headers = this.authHeaders();
			const resolvedHours = normalizeHoursValue(hours);
			const trackTime = resolvedHours === null;
			const fixedHours = trackTime ? null : resolvedHours;
			const payload = {
				date,
				journal: journalId,
				building: buildingId || null,
				supplier: supplierId || null,
				service: serviceId || null,
				flatRef: flatRef || '',
				hours: resolvedHours,
				trackTime,
				fixedHours,
				status: status || undefined,
			};
			const response = await axios.post(
				`${STRAPI_URL}/api/journal-appointments-items`,
				{ data: payload },
				{
					headers,
					params: {
						'populate[building][fields][0]': 'name',
						'populate[building][fields][1]': 'address',
						'populate[supplier][fields][0]': 'name',
						'populate[supplier][fields][1]': 'rightToWorkEngagementType',
						'populate[supplier][fields][2]': 'engagementHistory',
						'populate[service][fields][0]': 'name',
						'populate[service][fields][1]': 'rate',
						'populate[service][fields][2]': 'supplierRate',
						'populate[service][fields][3]': 'employeeRate',
						'populate[journal][fields][0]': 'name',
						'populate[time_entries][fields][0]': 'startAt',
						'populate[time_entries][fields][1]': 'endAt',
						'populate[time_entries][fields][2]': 'durationMinutes',
						'populate[time_entries][fields][3]': 'durationSeconds',
						'populate[time_entries][fields][4]': 'stopLocation',
						'populate[time_entries][fields][5]': 'flagCodes',
						'populate[time_entries][fields][6]': 'startLocation',
					},
				},
			);
			const created = normalizeAppointment(response?.data?.data || {});
			const normalizedDate = normalizeIsoDate(created.date);
			const normalizedBuildingId = created.building?.id || null;
			emitEvent('appointments:changed', {
				dates: normalizedDate ? [normalizedDate] : [],
				buildingId: normalizedBuildingId,
				buildingIds: normalizedBuildingId ? [String(normalizedBuildingId)] : [],
				appointmentIds: toUniqueStrings([created.id, created.documentId]),
			});
			return created;
		},

		async deleteAppointment(documentId, appointment = null) {
			if (!documentId) return false;
			const headers = this.authHeaders();
			await axios.delete(`${STRAPI_URL}/api/journal-appointments-items/${documentId}`, {
				headers,
			});
			const normalizedAppointmentId = appointment?.id ?? null;
			const normalizedDocumentId = appointment?.documentId ?? documentId;
			const normalizedDate = normalizeIsoDate(appointment?.date);
			const normalizedBuildingId = appointment?.building?.id ?? appointment?.buildingId ?? null;
			emitEvent('appointments:changed', {
				dates: normalizedDate ? [normalizedDate] : [],
				buildingId: normalizedBuildingId,
				buildingIds: normalizedBuildingId ? [String(normalizedBuildingId)] : [],
				appointmentIds: toUniqueStrings([normalizedAppointmentId, normalizedDocumentId]),
			});
			return true;
		},

		async updateAppointment(documentId, payload, previousAppointment = null) {
			if (!documentId) {
				throw new Error('Missing appointment identifier');
			}
			const headers = this.authHeaders();
			const normalizedPayload = { ...payload };
			if ('hours' in normalizedPayload) {
				normalizedPayload.hours = normalizeHoursValue(normalizedPayload.hours);
			}
			if ('fixedHours' in normalizedPayload) {
				normalizedPayload.fixedHours = normalizeHoursValue(normalizedPayload.fixedHours);
			}
			// Ensure the journal relation is preserved when updating an appointment.
			const response = await axios.put(
				`${STRAPI_URL}/api/journal-appointments-items/${documentId}`,
				{ data: normalizedPayload },
				{
					headers,
					params: {
						'populate[building][fields][0]': 'name',
						'populate[building][fields][1]': 'address',
						'populate[supplier][fields][0]': 'name',
						'populate[supplier][fields][1]': 'rightToWorkEngagementType',
						'populate[supplier][fields][2]': 'engagementHistory',
						'populate[service][fields][0]': 'name',
						'populate[service][fields][1]': 'rate',
						'populate[service][fields][2]': 'supplierRate',
						'populate[service][fields][3]': 'employeeRate',
						'populate[journal][fields][0]': 'name',
						'populate[time_entries][fields][0]': 'startAt',
						'populate[time_entries][fields][1]': 'endAt',
						'populate[time_entries][fields][2]': 'durationMinutes',
						'populate[time_entries][fields][3]': 'durationSeconds',
						'populate[time_entries][fields][4]': 'stopLocation',
						'populate[time_entries][fields][5]': 'flagCodes',
						'populate[time_entries][fields][6]': 'startLocation',
					},
				},
			);
			const updated = normalizeAppointment(response?.data?.data || {});
			const previousDate = normalizeIsoDate(previousAppointment?.date);
			const nextDate = normalizeIsoDate(updated.date);
			const previousBuildingId =
				previousAppointment?.building?.id ?? previousAppointment?.buildingId ?? null;
			const nextBuildingId = updated.building?.id || null;
			const appointmentIds = toUniqueStrings([
				previousAppointment?.id,
				previousAppointment?.documentId,
				documentId,
				updated.id,
				updated.documentId,
			]);
			emitEvent('appointments:changed', {
				dates: toUniqueStrings([previousDate, nextDate]),
				buildingId: nextBuildingId,
				buildingIds: toUniqueStrings([previousBuildingId, nextBuildingId]),
				appointmentIds,
			});
			return updated;
		},
	},
});
